import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { CheckOutlined, CloseOutlined } from '@material-ui/icons'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { DefinitiveQuantityField } from 'components/DefinitiveInputs/DefinitiveQuantityField'
import { DefinitiveInputValidators } from 'components/DefinitiveInputs/Validators'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { TimeString } from 'model/Quantity'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberFormatProps } from 'react-number-format'
import { useAppSelector } from 'store/configureStore'
import {
  ManuallySetProductionTimeArgs,
  PriceRowDto,
  PriceScope,
  QuantityDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useFinancialDetailsInputStyles } from './FinancialDetailsInput.styles'

export const ProdTimeInput = (props: {
  bomItemId: string
  priceRow: PriceRowDto
  priceScope: 'perUnit' | 'total'
  handleSaveManuallySetProductionTime: (
    rowId: string,
    args: ManuallySetProductionTimeArgs
  ) => Promise<void>
  handleResetProductionTime: (
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  loading?: Record<string, boolean>
  variant?: 'body2' | 'caption'
}) => {
  const classes = useFinancialDetailsInputStyles()
  const { t } = useTranslation()

  const [editing, setEditing] = useState(false)
  const [changed, setChanged] = useState(false)
  const [productionTime, setProductionTime] = useState(
    props.priceRow?.productionTime
  )

  useEffect(() => {
    setProductionTime(
      props.priceRow?.productionTimeManuallySet ||
        props.priceRow?.productionTime
    )
    return () => {
      setProductionTime(undefined)
    }
  }, [
    props.priceRow?.productionTimeManuallySet,
    props.priceRow?.productionTime,
  ])

  const getValueOrDefault = (productionTimeArg) =>
    !productionTimeArg || isNaN(productionTimeArg.value)
      ? { value: 0, unit: 'sec' }
      : productionTimeArg

  const handleChange = (newQuantity: QuantityDto) => {
    setChanged(true)
    setProductionTime(newQuantity)
  }

  const handleSave = async (e) => {
    try {
      e.preventDefault()
      if (productionTime.value > 0 && Boolean(productionTime.unit)) {
        await props.handleSaveManuallySetProductionTime(props.bomItemId, {
          primaryWorkingStep: props.priceRow.workingStep.primaryWorkingStep,
          secondaryWorkingStep: props.priceRow.workingStep.secondaryWorkingStep,
          productionTime: productionTime,
          priceScope:
            props.priceScope === 'perUnit' ? PriceScope.Unit : PriceScope.Total,
        })

        setEditing(false)
        setChanged(false)
      }
    } catch (ex) {
      ShowException('Failed to edit production time', ex)
    }
  }

  const projectIsEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector
  )

  const handleClose = () => {
    setEditing(false)
    setChanged(false)
    setProductionTime(
      props.priceRow?.productionTimeManuallySet ||
        props.priceRow?.productionTime
    )
  }

  // shared costs should not be editable
  if (!editing || props.priceRow.costsAreShared) {
    const data = getValueOrDefault(productionTime)
    if (data.value === null) {
      data.value = 0
    }

    return (
      <div style={{ textAlign: 'right', position: 'relative', width: '100%' }}>
        <Tooltip
          title={
            <>
              {`${t('project:prod-time')}: ${productionTime.value || 0} ${
                productionTime.unit
              }`}{' '}
            </>
          }
        >
          <span style={{ textAlign: 'inherit' }}>
            <ValueLabel
              onClick={
                !projectIsEditable || props.priceRow.costsAreShared
                  ? undefined
                  : () => setEditing(true)
              }
              valueProps={{
                variant: props.variant || 'body2',
              }}
              // label={'prod time'}
              value={TimeString(getValueOrDefault(productionTime))}
            />
          </span>
        </Tooltip>

        {props.priceRow.productionTimeManuallySet && (
          <Tooltip
            title={
              <Typography variant="caption">
                {t('project:reset-production-time', {
                  time: TimeString(
                    getValueOrDefault(props.priceRow.productionTime)
                  ),
                })}
              </Typography>
            }
          >
            <Typography
              variant="caption"
              component="p"
              style={{ position: 'absolute', right: 0, bottom: '-1.25em' }}
              className={classes.resetValueButton}
              onClick={() =>
                props.handleResetProductionTime(
                  props.bomItemId,
                  props.priceRow.workingStep.primaryWorkingStep,
                  props.priceRow.workingStep.secondaryWorkingStep
                )
              }
            >
              {props.loading[
                `delete-manual-production-time-${props.priceRow.workingStep.primaryWorkingStep}-${props.priceRow.workingStep.secondaryWorkingStep}`
              ] ? (
                <CircularProgress size={16} />
              ) : (
                TimeString(getValueOrDefault(props.priceRow.productionTime))
              )}
            </Typography>
          </Tooltip>
        )}
      </div>
    )
  } else {
    return (
      <form onSubmit={handleSave}>
        <div
          style={{
            display: 'flex',
            gap: '.5rem',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <DefinitiveQuantityField
            onChange={(newQuantity: QuantityDto) => handleChange(newQuantity)}
            quantityDto={productionTime}
            typographyVariant={props.variant || 'body2'}
            disableWarningsIcon
            numberFormatProps={
              {
                decimalScale: 0,
                allowNegative: false,
                min: 0.01,
                thousandSeparator: true,
              } as NumberFormatProps
            }
            selectTextOnFirstRender
            isAllowed={DefinitiveInputValidators.IsMinMaxAllowed(0, 999999)}
            validation={(quantityDto) => {
              const validations = DefinitiveInputValidators.CombineValidators([
                DefinitiveInputValidators.IsRequired(quantityDto.value),
                DefinitiveInputValidators.MinValue(quantityDto.value, 0),
                DefinitiveInputValidators.MaxValue(
                  quantityDto.value,
                  1_000_000
                ),
              ])

              if (validations?.length > 0) {
                setChanged(false)
              }

              return validations
            }}
          />

          {props.loading[
            `manual-estimation-${props.priceRow.workingStep.primaryWorkingStep}-${props.priceRow.workingStep.secondaryWorkingStep}`
          ] ? (
            <CircularProgress size={20} style={{ display: 'inline-flex' }} />
          ) : (
            <>
              <IconButton
                type="submit"
                color={'primary'}
                size="small"
                disabled={!changed}
              >
                <CheckOutlined />
              </IconButton>
              <IconButton type="reset" onClick={handleClose} size="small">
                <CloseOutlined />
              </IconButton>
            </>
          )}
        </div>
      </form>
    )
  }
}
